import { debug } from 'app/infrastructure/log/index';
import { isBrowser } from 'app/infrastructure/universal/runtime';
import get from 'lodash/get';
import { ApplicationState } from 'app/types-store';
import { AppDispatch } from 'app/infrastructure/configure-store';
import { Store } from 'redux';

// Time before background save will be performed in ms
export const backgroundSaveFrequency = 5000;

// An improvement worth investigating altho time consuming, is to
// create middleware that debounces every VALUE_CHANGE instead of using a fixed timer interval.

const isIdle = (state: EditableState) => !get(state, 'editing.fetching');

const hasChanges = (state: EditableState) => get(state, 'editing.hasChanges');

const submitIsPending = (state: EditableState) => get(state, 'editing.submitting');

const forcedSaveIsPending = (state: EditableState) => get(state, 'editing.immediateSaveRequests');

const shouldSaveOnNextInterval = (state: EditableState) => 
  isIdle(state) && hasChanges(state) && !submitIsPending(state) && !forcedSaveIsPending(state);

const shouldSaveImmediatly = (state: EditableState) => 
  isIdle(state) && hasChanges(state) && (submitIsPending(state) || forcedSaveIsPending(state));

const shouldSubmit = (state: EditableState) => 
  isIdle(state) && !hasChanges(state) && submitIsPending(state);

export const createSaveMiddleware = (
  saveCallback: () => (dispatch: AppDispatch, getState: () => ApplicationState) => void,
  submitCallback: () => (dispatch: AppDispatch, getState: () => ApplicationState) => void,
  area: string) => {

  let timer: any;

  const saveAction = (store: Store<ApplicationState>) => {

    const uiState: EditableState = store.getState()[area];

    if (hasChanges(uiState)) {
      debug('saving data for ' + area);
      saveCallback()(store.dispatch, store.getState);
    }
  };

  const removeTimer = () => {

    if (timer) {

      debug('removeTimer for ' + area);
      clearInterval(timer);
      timer = undefined;
    }
  };

  const addTimer = (store: Store<ApplicationState>) => {

    if (timer) {
      return;
    }

    debug('addTimer for ' + area);
    timer = setInterval(() => {
      saveAction(store);
    }, backgroundSaveFrequency);

  };

  return (store: Store<ApplicationState>) => (next: any) => (action: any) => {

    // next middleware is called first, as the state needed to be updated
    next(action);

    if (!isBrowser) {
      return;
    }

    const state: EditableState = store.getState()[area];

    if (shouldSubmit(state)) {
      debug('submit data for ' + area);
      submitCallback()(store.dispatch, store.getState);
      return;
    }

    if (shouldSaveImmediatly(state)) {
      removeTimer();
      saveAction(store);
      return;
    }

    if (shouldSaveOnNextInterval(state)) {
      addTimer(store);
      return;
    }

    removeTimer();
  };

};

interface EditableState {
  editing: {
    /** 
     * True if user has requested the data to be submitted
     */
    submitting: boolean;
    /** 
     * True if data is fetching or a save is running (I knoow...)
     */
    fetching: boolean;
    /**
     * Incremented but never more than 2, each time a forced save is requested by the application
     */
    immediateSaveRequests: 0|1|2;
    /**
     * True if there is a diff between persisted state, and current state
     */
    hasChanges: boolean;
  };
}
